import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  CheckCircle2,
  Infinity as InfinityIcon,
  Loader2,
  ShoppingCart,
  User as UserIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { FierroIcon } from '@/components/icons/FierroIcon';
import { Brand } from '@/components/Brand';
import { SiteFooter } from '@/components/layout/SiteFooter';
import { CartButton } from '@/features/billing/CartButton';
import { CartDrawer } from '@/features/billing/CartDrawer';
import { useBuyPlan } from '@/features/billing/useBuyPlan';
import { InsufficientTokensModal } from '@/features/billing/InsufficientTokensModal';
import { type Plan, formatPrice } from '@/features/billing/types';
import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/authStore';

// Imagen promocional por slug — los archivos viven en /public/planes/.
// Cuando se agregue un plan nuevo basta con dejar la imagen ahí y mapearla.
const PLAN_IMAGES: Record<string, string> = {
  trial:      '/planes/Alphaconcretogratis.png',
  pack_20:    '/planes/Alphaconcreto20fierros.png',
  pack_50:    '/planes/Alphaconcreto50fierros.png',
  pack_150:   '/planes/Alphaconcretoplan150fierros.png',
  sub_month:  '/planes/Alphaconcretoplanmensual.png',
  sub_3meses: '/planes/Alphaconcretoplan3meses.png',
  sub_6meses: '/planes/Alphaconcretoplan6meses.png',
  sub_year:   '/planes/Alphaconcretoplananual.png',
};
import { useCartStore } from '@/stores/cartStore';

/**
 * /planes — public catalog of plans.
 *
 * Visitors land here from the marketing site or the "Sin saldo" modal.
 * - Logged-in users see "Comprar" buttons → they navigate to /app/billing
 *   with the plan pre-added to the cart (set via location state).
 * - Anonymous visitors see "Crear cuenta" → /login with the plan id in the
 *   `?plan=` query so the post-login redirect lands on /app/billing.
 */
export function PlansPage() {
  const user = useAuthStore((s) => s.user);
  const isLoggedIn = !!useAuthStore((s) => s.token);
  const plansQuery = useQuery({
    queryKey: ['billing', 'public-plans'],
    queryFn: async () => (await api.get<Plan[]>('/api/billing/plans')).data,
  });
  const buyPlan = useBuyPlan();
  const [successPlan, setSuccessPlan] = useState<string | null>(null);

  return (
    <div className="min-h-screen" style={{ background: 'var(--surface-base)' }}>
      <header
        className="flex h-16 items-center border-b px-6"
        style={{ borderColor: 'var(--border-subtle)' }}
      >
        <Link to="/" className="no-underline">
          <Brand size="md" />
        </Link>
        <div className="ml-auto flex items-center gap-2">
          <CartButton />
          {isLoggedIn && user ? (
            <Link to="/app/billing" className="no-underline">
              <div
                className="flex h-9 items-center gap-2 rounded-[var(--radius-pill)] border px-3 text-xs"
                style={{ background: 'var(--surface-2)', borderColor: 'var(--border-default)', color: 'var(--text-primary)' }}
                title={user.email}
              >
                <span
                  className="flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-bold"
                  style={{ background: 'var(--accent)', color: 'var(--surface-base)' }}
                >
                  {(user.name || user.email).slice(0, 1).toUpperCase()}
                </span>
                <span className="font-mono tabular">{user.name || user.email}</span>
              </div>
            </Link>
          ) : (
            <>
              <Link to="/login" className="no-underline">
                <Button variant="ghost" size="md">Iniciar sesión</Button>
              </Link>
              <Link to="/login?tab=register" className="no-underline">
                <Button variant="primary" size="md">Crear cuenta</Button>
              </Link>
            </>
          )}
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-12">
        <div className="mb-10 text-center">
          <div className="text-[11px] uppercase tracking-[0.18em]" style={{ color: 'var(--text-muted)' }}>
            Planes y precios
          </div>
          <h1 className="mt-1 text-4xl">
            Compra <span style={{ color: 'var(--accent)' }}>fierros</span> o suscríbete
          </h1>
          <p className="mx-auto mt-3 max-w-xl text-sm" style={{ color: 'var(--text-secondary)' }}>
            Las exportaciones de PDF y Excel consumen "fierros" (entre 1 y 2 por archivo). Las
            suscripciones desbloquean exportaciones ilimitadas mientras estén activas.
          </p>
        </div>

        {plansQuery.isLoading ? (
          <div className="text-center" style={{ color: 'var(--text-muted)' }}>
            Cargando planes…
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-5">
            {plansQuery.data?.map((plan) => (
              <PublicPlanCard
                key={plan.id}
                plan={plan}
                isLoggedIn={isLoggedIn}
                busy={buyPlan.busyPlanId === plan.id}
                ready={buyPlan.ready}
                onBuy={async () => {
                  const r = await buyPlan.buy(plan);
                  if (r) setSuccessPlan(plan.name);
                }}
              />
            ))}
          </div>
        )}

        {buyPlan.error && (
          <div
            className="mx-auto mt-6 max-w-xl rounded-[var(--radius-md)] border p-3 text-center text-[13px]"
            style={{ background: 'rgba(220,38,38,0.08)', borderColor: 'rgba(220,38,38,0.4)', color: '#fca5a5' }}
          >
            {buyPlan.error}
          </div>
        )}
        {successPlan && (
          <div
            className="mx-auto mt-6 max-w-xl rounded-[var(--radius-md)] border p-3 text-center text-[13px]"
            style={{ background: 'rgba(34,197,94,0.08)', borderColor: 'rgba(34,197,94,0.4)', color: '#86efac' }}
          >
            <CheckCircle2 size={14} strokeWidth={1.8} className="mr-1 inline-block" />
            <strong>{successPlan}</strong> activado. Ve a{' '}
            <Link to="/app/billing" className="underline">
              tu cuenta
            </Link>{' '}
            para ver tu nuevo saldo.
          </div>
        )}

        <div className="mt-12 text-center text-[12px]" style={{ color: 'var(--text-muted)' }}>
          Pagos seguros procesados por Culqi (Niubiz/VisaNet). Aceptamos tarjeta y Yape.
        </div>
      </main>
      <SiteFooter />
      {/* Surface 402 modal here too — a guest who somehow hits a billing
          endpoint should still see a clean error. */}
      <InsufficientTokensModal />
      <CartDrawer />
    </div>
  );
}

function PublicPlanCard({
  plan,
  isLoggedIn,
  busy,
  ready,
  onBuy,
}: {
  plan: Plan;
  isLoggedIn: boolean;
  busy: boolean;
  ready: boolean;
  onBuy: () => void;
}) {
  const isTrial = plan.kind === 'trial';
  const featured = plan.is_subscription;
  const addToCart = useCartStore((s) => s.add);
  const imageSrc = PLAN_IMAGES[plan.slug];
  return (
    <div
      className="relative flex flex-col overflow-hidden rounded-[var(--radius-md)] border transition-transform hover:-translate-y-0.5"
      style={{
        background: 'var(--surface-1)',
        borderColor: featured ? 'var(--accent)' : 'var(--border-default)',
        boxShadow: featured ? '0 0 0 1px var(--accent)' : 'none',
      }}
    >
      {imageSrc && (
        <div
          className="relative h-[160px] w-full overflow-hidden"
          style={{ background: 'var(--surface-2)' }}
        >
          <img
            src={imageSrc}
            alt={plan.name}
            loading="lazy"
            className="h-full w-full object-cover"
            style={{ display: 'block' }}
            onError={(e) => {
              // Si falta la imagen, oculta el contenedor sin romper el card.
              (e.currentTarget.parentElement as HTMLElement).style.display = 'none';
            }}
          />
        </div>
      )}
      <div className="flex flex-1 flex-col p-6">
      {featured && (
        <span
          className="absolute right-4 top-3 rounded-[var(--radius-pill)] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider"
          style={{
            background: 'var(--accent)',
            color: 'var(--surface-base)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
          }}
        >
          Recomendado
        </span>
      )}
      <div className="mb-1 text-[10px] uppercase tracking-[0.14em]" style={{ color: 'var(--text-muted)' }}>
        {plan.is_subscription ? 'Suscripción' : isTrial ? 'Sin tarjeta' : 'Pack único'}
      </div>
      <h3 className="text-lg" style={{ fontFamily: 'var(--font-display)', fontWeight: 700 }}>
        {plan.name}
      </h3>
      <p className="mt-1 text-[12.5px]" style={{ color: 'var(--text-secondary)', lineHeight: 1.55 }}>
        {plan.description}
      </p>
      <div className="mt-4 flex items-baseline gap-2">
        <span className="font-mono tabular text-3xl" style={{ color: 'var(--text-primary)' }}>
          {isTrial ? 'Gratis' : formatPrice(plan.price_cents, plan.currency)}
        </span>
        {plan.is_subscription && (
          <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
            / {plan.duration_days === 365 ? 'año' : `${plan.duration_days} días`}
          </span>
        )}
      </div>
      <div className="mt-3 flex items-center gap-1 text-[13px]">
        {plan.is_subscription ? (
          <>
            <InfinityIcon size={14} strokeWidth={2} style={{ color: 'var(--accent)' }} />
            <span style={{ color: 'var(--text-secondary)' }}>Exportaciones ilimitadas</span>
          </>
        ) : (
          <>
            <FierroIcon size={14} />
            <span style={{ color: 'var(--text-secondary)' }}>
              {plan.tokens} fierros — {plan.tokens / 2} PDFs aprox.
            </span>
          </>
        )}
      </div>
      <div className="mt-auto flex flex-col gap-2 pt-6">
        {isTrial ? (
          <Link to="/login?tab=register" className="no-underline">
            <Button variant="secondary" size="md" className="w-full">
              Crear cuenta
            </Button>
          </Link>
        ) : (
          <>
            {/* Primary CTA — adds the plan to the cart and slides the
                drawer open. Works for guests too (they'll be bounced to
                /login from inside the drawer when they hit "Pagar"). */}
            <Button
              variant="primary"
              size="md"
              className="w-full"
              onClick={() => {
                addToCart(plan);
                toast.success(
                  `"${plan.name}" añadido al carrito`,
                  {
                    action: {
                      label: 'Ver carrito',
                      onClick: () => {
                        window.location.href = '/carrito';
                      },
                    },
                    duration: 4000,
                  },
                );
              }}
            >
              <ShoppingCart size={14} strokeWidth={1.8} />
              Añadir al carrito
            </Button>
            {/* Secondary — impulse buy: a single Culqi charge with no
                cart detour. Only shown to logged-in users. */}
            {isLoggedIn ? (
              <button
                type="button"
                onClick={onBuy}
                disabled={busy || !ready}
                className="text-[12px] underline-offset-2 hover:underline disabled:cursor-not-allowed disabled:opacity-50"
                style={{ color: 'var(--text-muted)' }}
                title={!ready ? 'Cargando procesador…' : 'Comprar ahora con un cargo'}
              >
                {busy ? 'Procesando…' : 'o comprar ahora'}
              </button>
            ) : (
              <Link
                to={`/login?tab=register&plan=${plan.id}`}
                className="text-center text-[12px] no-underline underline-offset-2 hover:underline"
                style={{ color: 'var(--text-muted)' }}
              >
                <UserIcon size={11} strokeWidth={1.8} className="-mt-0.5 mr-1 inline-block" />
                Crear cuenta para suscribirte
              </Link>
            )}
          </>
        )}
      </div>
      </div>
    </div>
  );
}
